import {
	Color,
	defaultFilterVert,
	Filter,
	GlProgram,
	Point,
	Texture,
	TexturePool,
} from 'pixi.js';
import {portalNormal, portalTangent} from './portalMath.js';

// Screen-space pixels. This is the approximate rotational travel near the
// portal's long edge rather than a generic directional displacement.
export const PORTAL_SCREEN_BEND_STRENGTH = 18;
// These three values are the useful visual tuning controls for the carried
// pixels: feedback controls lifetime, twist controls curl and pull controls how
// quickly the trail reaches the throat. A smaller pull keeps it orbiting longer.
export const PORTAL_SCREEN_HISTORY_FEEDBACK = 0.992;
export const PORTAL_SCREEN_HISTORY_TWIST = 0.84;
export const PORTAL_SCREEN_HISTORY_PULL = 0.008;
export const PORTAL_SCREEN_HISTORY_COLOR_GAIN = 1.0;
// Let the refraction feather past the rendered rim instead of ending exactly
// on it. This also keeps the complete portal surface inside the strong region.
export const PORTAL_SCREEN_BEND_OVERFLOW = 1.12;
const PORTAL_VISUAL_WIDTH_RATIO = 0.252;
const PORTAL_VISUAL_HEIGHT_SCALE = 1.12;

const FRAGMENT = `
precision highp float;

in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uHistory;
uniform vec4 uInputSize;
uniform vec4 uInputClamp;
uniform vec4 uHistorySize;
uniform vec4 uHistoryClamp;

uniform float uTime;
uniform float uStrength;
uniform float uFeedback;
uniform float uHistoryReady;
uniform float uActiveA;
uniform float uActiveB;
uniform vec2 uCenterA;
uniform vec2 uCenterB;
uniform vec2 uNormalA;
uniform vec2 uNormalB;
uniform vec2 uTangentA;
uniform vec2 uTangentB;
uniform vec2 uRadiiA;
uniform vec2 uRadiiB;
uniform vec3 uColorA;
uniform vec3 uColorB;

vec2 portalBend(
	vec2 pixel,
	vec2 center,
	vec2 normalAxis,
	vec2 tangentAxis,
	vec2 radii,
	float active,
	float phase,
	float twistScale,
	float inwardPull
) {
	if(active < 0.5) return vec2(0.0);

	vec2 delta = pixel - center;
	vec2 local = vec2(
		dot(delta, normalAxis) / max(radii.x, 1.0),
		dot(delta, tangentAxis) / max(radii.y, 1.0)
	);
	float radius = length(local);
	if(radius >= 1.0) return vec2(0.0);

	// Inverse-rotate the source pixels around the exact portal center. This makes
	// the rendered result genuinely rotate clockwise instead of merely receiving
	// a tangential nudge. The angle increases toward the interior like a vortex.
	float rimFade = 1.0 - smoothstep(0.86, 1.0, radius);
	float throatFade = smoothstep(0.025, 0.13, radius);
	float envelope = rimFade * throatFade;
	float angle = atan(local.y, local.x);
	float spiralFlow = 0.88 + 0.12 * sin(
		radius * 18.0 - angle * 2.0 + uTime * 2.0 + phase
	);
	float interiorTwist = 0.72 + (1.0 - radius) * 0.85;
	float twistRadians = uStrength / max(radii.y, 1.0)
		* interiorTwist * envelope * spiralFlow * twistScale;
	float inverseAngle = -twistRadians;
	float cosine = cos(inverseAngle);
	float sine = sin(inverseAngle);
	vec2 rotatedLocal = vec2(
		local.x * cosine - local.y * sine,
		local.x * sine + local.y * cosine
	);
	// Sampling progressively farther from the center makes the visible pixels
	// move inward. History uses a much larger pull than the live frame.
	rotatedLocal *= 1.0 + inwardPull * envelope * (1.0 - radius * 0.25);
	// Rebuild the offset in the portal's own elliptical basis. Rotating raw screen
	// pixels would describe a circle and push a narrow side-on portal toward its
	// tips instead of carrying pixels around its visible vortex.
	vec2 rotatedDelta = normalAxis * (rotatedLocal.x * radii.x)
		+ tangentAxis * (rotatedLocal.y * radii.y);
	return rotatedDelta - delta;
}

float portalInfluence(
	vec2 pixel,
	vec2 center,
	vec2 normalAxis,
	vec2 tangentAxis,
	vec2 radii,
	float active
) {
	if(active < 0.5) return 0.0;
	vec2 delta = pixel - center;
	vec2 local = vec2(
		dot(delta, normalAxis) / max(radii.x, 1.0),
		dot(delta, tangentAxis) / max(radii.y, 1.0)
	);
	float radius = length(local);
	if(radius >= 1.0) return 0.0;
	float rimFade = 1.0 - smoothstep(0.86, 1.0, radius);
	float throatFade = smoothstep(0.02, 0.12, radius);
	return rimFade * throatFade;
}

float portalColorAffinity(vec3 sampleColor, vec3 portalColor) {
	float sampleMin = min(sampleColor.r, min(sampleColor.g, sampleColor.b));
	float portalMin = min(portalColor.r, min(portalColor.g, portalColor.b));
	vec3 sampleChroma = sampleColor - vec3(sampleMin);
	vec3 portalChroma = portalColor - vec3(portalMin);
	float sampleStrength = max(sampleChroma.r, max(sampleChroma.g, sampleChroma.b));
	float portalStrength = max(portalChroma.r, max(portalChroma.g, portalChroma.b));
	vec3 sampleHue = sampleChroma / max(sampleStrength, 0.001);
	vec3 portalHue = portalChroma / max(portalStrength, 0.001);
	float hueDistance = length(sampleHue - portalHue);
	float chromaConfidence = smoothstep(0.025, 0.11, sampleStrength)
		* smoothstep(0.025, 0.11, portalStrength);
	return (1.0 - smoothstep(0.18, 0.62, hueDistance)) * chromaConfidence;
}

void main(void) {
	vec2 pixel = vTextureCoord * uInputSize.xy;
	float influenceA = portalInfluence(
		pixel, uCenterA, uNormalA, uTangentA, uRadiiA, uActiveA
	);
	float influenceB = portalInfluence(
		pixel, uCenterB, uNormalB, uTangentB, uRadiiB, uActiveB
	);
	float influence = max(influenceA, influenceB);
	// Most screen pixels lie outside both portals. They need one texture read,
	// without refraction, hue comparisons, or temporal-history sampling.
	if(influence <= 0.0) {
		finalColor = texture(uTexture, vTextureCoord);
		return;
	}
	vec2 currentOffset = portalBend(
		pixel, uCenterA, uNormalA, uTangentA, uRadiiA, uActiveA,
		0.0, 1.0, 0.006
	) + portalBend(
		pixel, uCenterB, uNormalB, uTangentB, uRadiiB, uActiveB,
		2.31, 1.0, 0.006
	);
	vec2 currentCoord = clamp(
		vTextureCoord + currentOffset * uInputSize.zw,
		uInputClamp.xy,
		uInputClamp.zw
	);
	vec4 currentColor = texture(uTexture, currentCoord);
	vec4 liveColor = texture(uTexture, vTextureCoord);
	// A full screen-space rotation can sample just beyond the thin portal mesh.
	// Blend most of the live selected-color surface back in while retaining a
	// visible fraction of its bend; scenery and travellers remain fully warped.
	float livePortalSurface = max(
		influenceA * portalColorAffinity(liveColor.rgb, uColorA),
		influenceB * portalColorAffinity(liveColor.rgb, uColorB)
	);
	currentColor = mix(currentColor, liveColor, livePortalSurface * 0.90);
	if(uHistoryReady < 0.5) {
		finalColor = currentColor;
		return;
	}

	vec2 historyOffset = portalBend(
		pixel, uCenterA, uNormalA, uTangentA, uRadiiA, uActiveA,
		0.0, ${PORTAL_SCREEN_HISTORY_TWIST}, ${PORTAL_SCREEN_HISTORY_PULL}
	) + portalBend(
		pixel, uCenterB, uNormalB, uTangentB, uRadiiB, uActiveB,
		2.31, ${PORTAL_SCREEN_HISTORY_TWIST}, ${PORTAL_SCREEN_HISTORY_PULL}
	);
	vec2 historyPixel = pixel + historyOffset;
	// Advect from exact texel centers. Linear resampling a rotating history every
	// frame quickly dissolves sprite fragments into the portal; pixel-center
	// transport keeps recognizable pieces alive and gives the suction a crisp,
	// liquid-pixel character.
	vec2 historySamplePixel = floor(historyPixel) + vec2(0.5);
	vec2 historyCoord = clamp(
		historySamplePixel * uHistorySize.zw,
		uHistoryClamp.xy,
		uHistoryClamp.zw
	);
	vec4 historyColor = texture(uHistory, historyCoord);
	vec2 currentHistoryCoord = clamp(
		historySamplePixel * uInputSize.zw,
		uInputClamp.xy,
		uInputClamp.zw
	);
	vec4 currentHistoryBase = texture(uTexture, currentHistoryCoord);
	// Compare the advected history to the same place in the current scene. Stable
	// portal/background pixels largely cancel, while pixels left behind by a
	// moving traveller remain as a strong temporal residue that can keep spiralling.
	float historyDelta = length(historyColor.rgb - currentHistoryBase.rgb)
		+ abs(historyColor.a - currentHistoryBase.a) * 0.5;
	// Keep lower-contrast remnants alive so a traveller continues to curl through
	// several revolutions instead of disappearing as soon as its first trail fades.
	float movingResidue = smoothstep(0.012, 0.12, historyDelta);
	// The procedural portal intentionally changes brightness from band to band,
	// but all of those shades share its selected hue. Do not mistake that motion
	// for a travelling sprite or recursively bleach the surface into the scenery.
	float stablePortalA = influenceA
		* portalColorAffinity(historyColor.rgb, uColorA);
	float stablePortalB = influenceB
		* portalColorAffinity(historyColor.rgb, uColorB);
	float stablePortal = clamp(max(stablePortalA, stablePortalB), 0.0, 1.0);
	movingResidue *= 1.0 - stablePortal;
	float historyMax = max(historyColor.r, max(historyColor.g, historyColor.b));
	float historyMin = min(historyColor.r, min(historyColor.g, historyColor.b));
	float foreignColoredHistory = smoothstep(0.07, 0.22, historyMax - historyMin)
		* (1.0 - stablePortal);
	// When the current pixel is the portal itself, admit only a distinctly colored
	// non-portal trail. This rejects neutral scenery sampled across the thin rim,
	// which was the source of the cumulative pale/transparent appearance.
	movingResidue *= mix(
		1.0,
		foreignColoredHistory,
		clamp(livePortalSurface, 0.0, 1.0)
	);
	// The live-frame vortex remains permanently active. Temporal accumulation is
	// reserved for actual moving residue so repeated sampling cannot wash out a
	// stationary portal surface over time.
	// Lift partially faded residue instead of linearly throttling it. The hue gate
	// above still rejects the portal itself, while traveller pixels remain visually
	// substantial during the later turns of the vortex.
	float historyActivity = max(
		pow(movingResidue, 0.35),
		foreignColoredHistory * 0.97
	);

	float historyWeight = uFeedback * influence * historyActivity;
	// Counter the repeated bilinear resampling that otherwise dissolves colorful
	// sprite pixels after only a few turns. The gain applies only to non-portal
	// chroma, so it cannot drive the selected portal surface toward white.
	vec4 carriedHistory = historyColor;
	vec3 carriedDelta = historyColor.rgb - currentHistoryBase.rgb;
	carriedHistory.rgb = mix(
		historyColor.rgb,
		clamp(
			currentHistoryBase.rgb + carriedDelta * ${PORTAL_SCREEN_HISTORY_COLOR_GAIN.toFixed(3)},
			0.0,
			1.0
		),
		foreignColoredHistory
	);
	vec4 temporalColor = mix(currentColor, carriedHistory, historyWeight);
	// The trail may add old sprite channels, but it must never erase the current
	// saturated portal into a translucent-looking average.
	temporalColor.rgb = max(temporalColor.rgb, currentColor.rgb * 0.96);
	temporalColor.rgb = max(
		temporalColor.rgb,
		liveColor.rgb * livePortalSurface * 0.94
	);
	temporalColor.a = max(temporalColor.a, currentColor.a);
	finalColor = temporalColor;
}`;

const COPY_FRAGMENT = `
precision highp float;

in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;

void main(void) {
	finalColor = texture(uTexture, vTextureCoord);
}`;

const vectorLength = vector => Math.hypot(vector.x, vector.y);

const normalize = vector => {
	const length = vectorLength(vector);
	return length > 0.0001 ? {x: vector.x / length, y: vector.y / length} : null;
};

const screenVector = (origin, point) => ({
	x: point.x - origin.x,
	y: point.y - origin.y,
});

// Conservative screen bounds of a rotated ellipse. Keep partially visible
// rims active; the shader performs the exact ellipse test per pixel.
export const portalFrameIntersectsViewport = (frame, viewport) => {
	if(!frame) return false;
	if(!viewport) return true;
	const extentX = Math.hypot(frame.normal.x * frame.radii.x, frame.tangent.x * frame.radii.y);
	const extentY = Math.hypot(frame.normal.y * frame.radii.x, frame.tangent.y * frame.radii.y);
	return frame.center.x + extentX >= viewport.x &&
		frame.center.x - extentX <= viewport.x + viewport.width &&
		frame.center.y + extentY >= viewport.y &&
		frame.center.y - extentY <= viewport.y + viewport.height;
};

export const getPortalScreenFrame = (endpoint, renderParent, ptm) => {
	const pixelScale = Number(ptm);
	const apertureHalfLength = Number(endpoint?.apertureHalfLength);
	if(!endpoint?.body || !renderParent?.toGlobal ||
		!Number.isFinite(pixelScale) || pixelScale <= 0 ||
		!Number.isFinite(apertureHalfLength) || apertureHalfLength <= 0) {
		return null;
	}
	const position = endpoint.body.GetPosition();
	const center = {
		x: position.get_x() * pixelScale,
		y: position.get_y() * pixelScale,
	};
	const normal = portalNormal(endpoint.body.GetAngle());
	const tangent = portalTangent(endpoint.body.GetAngle());
	const aperturePixels = Math.max(1, apertureHalfLength * pixelScale);
	const normalRadius = aperturePixels * PORTAL_VISUAL_WIDTH_RATIO
		* PORTAL_SCREEN_BEND_OVERFLOW;
	const tangentRadius = aperturePixels * PORTAL_VISUAL_HEIGHT_SCALE
		* PORTAL_SCREEN_BEND_OVERFLOW;
	const screenCenter = renderParent.toGlobal(center, new Point());
	const screenNormalPoint = renderParent.toGlobal({
		x: center.x + normal.x * normalRadius,
		y: center.y + normal.y * normalRadius,
	}, new Point());
	const screenTangentPoint = renderParent.toGlobal({
		x: center.x + tangent.x * tangentRadius,
		y: center.y + tangent.y * tangentRadius,
	}, new Point());
	const normalVector = screenVector(screenCenter, screenNormalPoint);
	const tangentVector = screenVector(screenCenter, screenTangentPoint);
	const normalAxis = normalize(normalVector);
	const tangentAxis = normalize(tangentVector);
	if(!normalAxis || !tangentAxis) return null;

	return {
		center: screenCenter,
		normal: normalAxis,
		tangent: tangentAxis,
		color: endpoint?.prefabObject?.settings?.color ||
			endpoint?.body?.mySprite?.data?.colorFill?.[0] || '#0093ff',
		radii: {
			x: vectorLength(normalVector),
			y: vectorLength(tangentVector),
		},
	};
};

export class PortalScreenBendFilter extends Filter {
	constructor(
		strength = PORTAL_SCREEN_BEND_STRENGTH,
		feedback = PORTAL_SCREEN_HISTORY_FEEDBACK,
	) {
		super({
			glProgram: GlProgram.from({
				vertex: defaultFilterVert,
				fragment: FRAGMENT,
				name: 'portal-screen-bend-filter',
			}),
			resources: {
				portalBendUniforms: {
					uTime: {value: 0, type: 'f32'},
					uStrength: {value: strength, type: 'f32'},
					uFeedback: {value: feedback, type: 'f32'},
					uHistoryReady: {value: 0, type: 'f32'},
					uActiveA: {value: 0, type: 'f32'},
					uActiveB: {value: 0, type: 'f32'},
					uCenterA: {value: new Float32Array(2), type: 'vec2<f32>'},
					uCenterB: {value: new Float32Array(2), type: 'vec2<f32>'},
					uNormalA: {value: new Float32Array([1, 0]), type: 'vec2<f32>'},
					uNormalB: {value: new Float32Array([1, 0]), type: 'vec2<f32>'},
					uTangentA: {value: new Float32Array([0, 1]), type: 'vec2<f32>'},
					uTangentB: {value: new Float32Array([0, 1]), type: 'vec2<f32>'},
					uRadiiA: {value: new Float32Array([1, 1]), type: 'vec2<f32>'},
					uRadiiB: {value: new Float32Array([1, 1]), type: 'vec2<f32>'},
					uColorA: {value: new Float32Array([0, 0.58, 1]), type: 'vec3<f32>'},
					uColorB: {value: new Float32Array([0, 0.58, 1]), type: 'vec3<f32>'},
					uHistorySize: {value: new Float32Array([1, 1, 1, 1]), type: 'vec4<f32>'},
					uHistoryClamp: {value: new Float32Array([0, 0, 1, 1]), type: 'vec4<f32>'},
				},
				uHistory: Texture.EMPTY.source,
			},
		});
		this.uniforms = this.resources.portalBendUniforms.uniforms;
		this.historyTextures = [];
		this.historyIndex = 0;
		this.historyReady = false;
		this.portalFrames = [null, null];
		this.portalColors = [null, null];
		// Pixi's PassthroughFilter eagerly creates a WebGPU program even in a
		// WebGL-only renderer. Keep this feedback copy explicitly WebGL-safe.
		this.copyFilter = new Filter({
			glProgram: GlProgram.from({
				vertex: defaultFilterVert,
				fragment: COPY_FRAGMENT,
				name: 'portal-screen-history-copy-filter',
			}),
		});
	}

	ensureHistory(input) {
		const width = input.frame.width;
		const height = input.frame.height;
		const resolution = input.source._resolution || 1;
		const createHistoryTextures = () => [0, 1].map(index => {
			const texture = TexturePool.getOptimalTexture(width, height, resolution, false);
			texture.source.label = `portal-screen-history-${index}`;
			return texture;
		});
		if(!this.historyTextures.length) {
			this.historyTextures = createHistoryTextures();
			this.historyReady = false;
			return;
		}
		const first = this.historyTextures[0];
		if(first.frame.width === width && first.frame.height === height &&
			first.source._resolution === resolution) {
			return;
		}
		for(const texture of this.historyTextures) TexturePool.returnTexture(texture);
		this.historyTextures = createHistoryTextures();
		this.historyIndex = 0;
		this.historyReady = false;
	}

	apply(filterManager, input, output, clearMode) {
		this.ensureHistory(input);
		const previous = this.historyTextures[this.historyIndex];
		const nextIndex = 1 - this.historyIndex;
		const next = this.historyTextures[nextIndex];
		this.resources.uHistory = previous.source;
		const historyWidth = previous.source.width;
		const historyHeight = previous.source.height;
		this.uniforms.uHistorySize.set([
			historyWidth,
			historyHeight,
			1 / historyWidth,
			1 / historyHeight,
		]);
		const halfPixelX = 0.5 / previous.source.pixelWidth;
		const halfPixelY = 0.5 / previous.source.pixelHeight;
		this.uniforms.uHistoryClamp.set([
			halfPixelX,
			halfPixelY,
			previous.frame.width / historyWidth - halfPixelX,
			previous.frame.height / historyHeight - halfPixelY,
		]);
		this.uniforms.uHistoryReady = this.historyReady ? 1 : 0;
		filterManager.applyFilter(this, input, next, true);
		this.copyFilter.blendMode = this.blendMode;
		this.copyFilter.apply(filterManager, next, output, clearMode);
		this.historyIndex = nextIndex;
		this.historyReady = true;
	}

	setTime(time) {
		this.uniforms.uTime = time;
	}

	setPortal(index, frame) {
		this.portalFrames[index] = frame;
		const suffix = index === 0 ? 'A' : 'B';
		this.uniforms[`uActive${suffix}`] = frame ? 1 : 0;
		if(!frame) return;
		this.uniforms[`uCenter${suffix}`].set([frame.center.x, frame.center.y]);
		this.uniforms[`uNormal${suffix}`].set([frame.normal.x, frame.normal.y]);
		this.uniforms[`uTangent${suffix}`].set([frame.tangent.x, frame.tangent.y]);
		this.uniforms[`uRadii${suffix}`].set([frame.radii.x, frame.radii.y]);
		if(this.portalColors[index] === frame.color) return;
		try {
			const [red, green, blue] = new Color(frame.color || '#0093ff').toArray();
			this.uniforms[`uColor${suffix}`].set([red, green, blue]);
			this.portalColors[index] = frame.color;
		} catch {
			// Keep the last valid portal hue if an editor field is temporarily invalid.
		}
	}

	updateVisibility(viewport) {
		this.enabled = this.portalFrames.some(frame => portalFrameIntersectsViewport(frame, viewport));
		if(!this.enabled) {
			// No screen capture, history pass, or copy pass while the pair is off
			// camera. Do not replay stale trails when the camera returns.
			this.historyReady = false;
			this.resources.uHistory = Texture.EMPTY.source;
			for(const texture of this.historyTextures) TexturePool.returnTexture(texture);
			this.historyTextures.length = 0;
			this.historyIndex = 0;
		}
	}

	destroy(destroyPrograms = false) {
		// The filter system's shared bind group still references the final input
		// after a pass. Returning history targets to Pixi's pool matches its normal
		// filter lifecycle; destroying them here would invalidate that live binding
		// and poison subsequent WebGL draws.
		const historyTextures = [...this.historyTextures];
		this.historyTextures.length = 0;
		this.copyFilter?.destroy(destroyPrograms);
		this.copyFilter = null;
		super.destroy(destroyPrograms);
		for(const texture of historyTextures) TexturePool.returnTexture(texture);
	}
}
